import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, TrendingUp, TrendingDown, Calendar } from 'lucide-react';

export interface TrendsData {
  bestDay: string | null;
  worstDay: string | null;
  dayTotals: Record<string, number>;
  dayCounts: Record<string, number>;
  totalTransactions: number;
}

// ── Internal SVG bar chart ─────────────────────────────────────────────────
const WeeklyBar = React.memo(({ data }: { data: TrendsData }) => {
  const DAY_FULL  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const DAY_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const entries = DAY_FULL.map((full, i) => ({
    label: DAY_SHORT[i],
    value: data.dayTotals[full] || 0,
    isBest:  full === data.bestDay,
    isWorst: full === data.worstDay,
  }));
  const maxVal  = Math.max(...entries.map(e => e.value), 1);
  const W = 680, H = 200, PAD_L = 44, PAD_R = 8, PAD_TOP = 26, PAD_BOT = 28;
  const usableW = W - PAD_L - PAD_R;
  const usableH = H - PAD_TOP - PAD_BOT;
  const slotW   = usableW / entries.length;
  const barW    = slotW * 0.56;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="wc-best" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d399" stopOpacity={0.95}/>
          <stop offset="100%" stopColor="#059669" stopOpacity={0.65}/>
        </linearGradient>
        <linearGradient id="wc-worst" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f87171" stopOpacity={0.85}/>
          <stop offset="100%" stopColor="#dc2626" stopOpacity={0.55}/>
        </linearGradient>
        <linearGradient id="wc-normal" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.75}/>
          <stop offset="100%" stopColor="#0891b2" stopOpacity={0.45}/>
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {[0.25, 0.5, 0.75, 1].map(frac => {
        const y   = PAD_TOP + usableH - frac * usableH;
        const val = Math.round(maxVal * frac);
        const lbl = val >= 1000 ? `${(val / 1000).toFixed(0)}K` : String(val);
        return (
          <g key={frac}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} stroke="rgba(6,182,212,0.08)" strokeWidth={0.6}/>
            <text x={PAD_L - 5} y={y + 3} textAnchor="end" fill="rgba(148,163,184,0.45)" fontSize={8} fontFamily="monospace">{lbl}</text>
          </g>
        );
      })}
      <line x1={PAD_L} x2={W - PAD_R} y1={PAD_TOP + usableH} y2={PAD_TOP + usableH} stroke="rgba(6,182,212,0.15)" strokeWidth={0.8}/>

      {/* Bars */}
      {entries.map((e, i) => {
        const cx   = PAD_L + i * slotW + slotW / 2;
        const barH = Math.max((e.value / maxVal) * usableH, e.value > 0 ? 4 : 0);
        const y    = PAD_TOP + usableH - barH;
        const fill = e.isBest ? 'url(#wc-best)' : (e.isWorst && e.value > 0) ? 'url(#wc-worst)' : 'url(#wc-normal)';
        return (
          <motion.g
            key={e.label}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 + i * 0.07 }}
          >
            <rect x={cx - barW / 2} y={y} width={barW} height={barH} fill={fill} rx={4}/>
            {e.isBest && (
              <text x={cx} y={y - 8} textAnchor="middle" fill="rgba(52,211,153,1)" fontSize={7.5} fontFamily="monospace" fontWeight="bold">▲BEST</text>
            )}
            {e.isWorst && e.value > 0 && (
              <text x={cx} y={y - 8} textAnchor="middle" fill="rgba(252,165,165,0.9)" fontSize={7.5} fontFamily="monospace">▼SLOW</text>
            )}
            <text
              x={cx}
              y={PAD_TOP + usableH + 16}
              textAnchor="middle"
              fill={e.isBest ? 'rgba(52,211,153,0.9)' : (e.isWorst && e.value > 0) ? 'rgba(252,165,165,0.8)' : 'rgba(148,163,184,0.65)'}
              fontSize={10}
              fontFamily="monospace"
              fontWeight={e.isBest ? 'bold' : 'normal'}
            >{e.label}</text>
          </motion.g>
        );
      })}
    </svg>
  );
});

// ── Exported standalone modal ──────────────────────────────────────────────
export default function WeeklyChartModal({ data, onClose }: { data: TrendsData | null; onClose: () => void }) {
  if (!data || !data.bestDay) return null;

  const bestRevenue  = data.dayTotals[data.bestDay]  || 0;
  const worstRevenue = data.worstDay ? (data.dayTotals[data.worstDay] || 0) : 0;

  return (
    <AnimatePresence>
      <motion.div
        key="wc-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#04060f]/88 backdrop-blur-xl"
        onClick={onClose}
      >
        <motion.div
          key="wc-card"
          initial={{ opacity: 0, scale: 0.88, y: 28 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 10 }}
          transition={{ type: 'spring', stiffness: 220, damping: 24 }}
          className="relative w-full max-w-2xl mx-4 bg-[#060a14]/98 backdrop-blur-3xl border border-cyan-500/20 rounded-3xl shadow-[0_0_80px_rgba(6,182,212,0.14)] p-5 sm:p-8"
          onClick={e => e.stopPropagation()}
        >
          {/* Scan lines */}
          <div
            className="absolute inset-0 rounded-3xl pointer-events-none overflow-hidden opacity-[0.025]"
            style={{ backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(6,182,212,0.3) 2px,rgba(6,182,212,0.3) 4px)' }}
          />

          {/* Header */}
          <div className="relative flex items-center justify-between mb-5">
            <div>
              <p className="text-[8px] uppercase font-mono text-cyan-500/40 tracking-[0.25em] mb-0.5">Luna Analytics · Sales Intelligence</p>
              <h2 className="text-[18px] font-bold text-white tracking-tight">Weekly Sales Patterns</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-white/5 border border-white/[0.08] flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all"
            >
              <X size={14}/>
            </button>
          </div>

          {/* Best / Worst day pills */}
          <div className="relative flex flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl bg-emerald-500/[0.08] border border-emerald-500/20">
              <TrendingUp size={14} className="text-emerald-400 shrink-0"/>
              <div>
                <p className="text-[8px] font-mono text-emerald-500/50 uppercase tracking-widest">Best Day</p>
                <p className="text-[15px] font-bold text-emerald-400 leading-none">{data.bestDay}</p>
              </div>
              <p className="text-[10px] font-mono text-emerald-400/50 ml-1">Rs.{bestRevenue.toLocaleString()}</p>
            </div>
            {data.worstDay && (
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl bg-red-500/[0.08] border border-red-500/20">
                <TrendingDown size={14} className="text-red-400 shrink-0"/>
                <div>
                  <p className="text-[8px] font-mono text-red-500/50 uppercase tracking-widest">Slowest Day</p>
                  <p className="text-[15px] font-bold text-red-400 leading-none">{data.worstDay}</p>
                </div>
                <p className="text-[10px] font-mono text-red-400/50 ml-1">Rs.{worstRevenue.toLocaleString()}</p>
              </div>
            )}
          </div>

          {/* Chart */}
          <div className="w-full" style={{ height: '200px' }}>
            <WeeklyBar data={data}/>
          </div>

          {/* Footer */}
          <div className="relative flex items-center justify-between mt-4 pt-3 border-t border-cyan-500/[0.08]">
            <div className="flex items-center gap-1.5 text-[9px] font-mono text-slate-600">
              <Calendar size={10}/>
              Based on {data.totalTransactions.toLocaleString()} lifetime transactions
            </div>
            <div className="flex items-center gap-3 text-[8px] font-mono text-slate-600">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-sm bg-emerald-500 inline-block"/>Best</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-sm bg-red-500 inline-block"/>Slow</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-sm bg-cyan-500 inline-block"/>Normal</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
